import { getSessionId } from '@/lib/trackerSession';
import { pickVariantIndex } from '@/lib/ctaVariantsShared';
// @ts-ignore — plain-JS Israel clock helpers shared with the worker
import { isEveningWindow, greetingNow } from '@/lib/israelHours.mjs';

// Evening-aware WhatsApp contact (Founder 14/10/26): after the evening window opens (Israel clock) the visitor still taps the
// WhatsApp button but hesitates to write at night. In the window: (a) the prefill opens with "ערב טוב, " (ONE composer:
// lib/heMsg.mjs leadText), (b) a short reassurance note (he_cta_variants key evening_note) sits by the CTA. The window comes
// from /api/evening-config (settings table); QA override: ?evening=1 forces on, ?evening=0 forces off (kept for the tab session).
export type EveningVariant = { id: number; label: string; copyText: string };
type Cfg = { enabled: boolean; start: string; end: string; variants: EveningVariant[] };
let cfg: Cfg | null = null;
let pending: Promise<Cfg | null> | null = null;

export function loadEveningConfig(): Promise<Cfg | null> {
  if (cfg) return Promise.resolve(cfg);
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (!pending) pending = fetch('/api/evening-config').then(r => r.json()).then((j: Cfg) => (cfg = j)).catch(() => { pending = null; return null; });
  return pending;
}

/** true / false when ?evening=1 / ?evening=0 (remembered for the tab session), null = no override. */
export function eveningOverride(): boolean | null {
  if (typeof window === 'undefined') return null;
  try {
    const q = new URLSearchParams(location.search).get('evening');
    if (q === '1' || q === '0') { try { sessionStorage.setItem('evening_qa', q); } catch {} return q === '1'; }
    const s = sessionStorage.getItem('evening_qa'); return s === '1' ? true : s === '0' ? false : null;
  } catch { return null; }
}

/** Is the evening mode on right now (sync; needs loadEveningConfig() to have resolved unless forced)? */
export function eveningActive(now = new Date()): boolean {
  const o = eveningOverride(); if (o !== null) return o;
  return !!cfg && cfg.enabled && isEveningWindow(now, cfg.start, cfg.end);
}

/** The greeting the prefill opens with: "ערב טוב" / "בוקר טוב" / null. Browser only — servers pass the greeting explicitly. */
export function currentGreeting(): string | null {
  if (typeof window === 'undefined') return null;
  if (!eveningActive()) return null;
  return greetingNow() || (eveningOverride() ? 'ערב טוב' : null);
}

/** The reassurance variant for this visitor (stable per tracker session). null outside the evening window. */
export function currentEveningVariant(): EveningVariant | null {
  if (!eveningActive() || !cfg?.variants?.length) return null;
  let seed = 'anon'; try { seed = getSessionId() || 'anon'; } catch {}
  return cfg.variants[pickVariantIndex(`evening_note:${seed}`, cfg.variants.length)] || null;
}
export const currentEveningVariantId = (): number | undefined => currentEveningVariant()?.id;

// Which note is on screen right now: logInquiry attributes the click to it (he_inquiries.evening_variant_id) and logs the CTA click.
let shown = 0;
let shownId: number | undefined;
export function markNoteShown(id: number | null) { if (id) { shown++; shownId = id; } else shown = Math.max(0, shown - 1); if (!shown) shownId = undefined; }
export const shownEveningVariantId = (): number | undefined => (shown > 0 ? shownId : undefined);
// "One note for the pair": in a LIST of pro cards only the first mounted note renders (ordered by DOM/effect order), not one per card.
const slots = new Set<string>();
export function claimSlot(kind: string): boolean { if (slots.has(kind)) return false; slots.add(kind); return true; }
export function releaseSlot(kind: string) { slots.delete(kind); }
